import React, { useMemo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { colors } from '../theme/colors';
import { useThemeColors } from '../hooks/useThemeColors';
import { getPolaroidShadowStyles } from '../theme/shadowStyles';

const POLAROID_IMAGE_RATIO = 4 / 5;

export interface PolaroidCardProps {
  /** Content for the photo/image area (e.g. icon, gradient). Omit when variant='summary'. */
  imageArea?: React.ReactNode | undefined;
  /** Content for the caption below (e.g. title, subtitle) */
  caption: React.ReactNode;
  /** Background color for the image area or accent strip; defaults to cerulean */
  accentColor?: string | undefined;
  /** Optional press handler; when set, card is pressable */
  onPress?: (() => void) | undefined;
  /** Optional long-press handler (e.g. for context menu) */
  onLongPress?: (() => void) | undefined;
  /** CSS transform rotation string (e.g. '-2deg') */
  rotation?: string | undefined;
  /** Card width; photo height = width * POLAROID_IMAGE_RATIO. Omit for full width (summary). */
  width?: number | undefined;
  /** Additional style for the polaroid container */
  style?: object | undefined;
  /** Placeholder style: dashed border, lighter look */
  placeholder?: boolean | undefined;
  /** 'default': full photo area + caption. 'summary': thin accent strip + content. */
  variant?: 'default' | 'summary' | undefined;
  /** Screen-reader label when the card is pressable. Defaults to a generic
   *  "Card" — pass a meaningful label (place name, day label, etc.). */
  accessibilityLabel?: string | undefined;
}

export function PolaroidCard({
  imageArea,
  caption,
  accentColor = colors.polaroidRainbow[0],
  onPress,
  onLongPress,
  rotation = '0deg',
  width = 300,
  style,
  placeholder = false,
  variant = 'default',
  accessibilityLabel,
}: PolaroidCardProps) {
  const themeColors = useThemeColors();
  const isSummary = variant === 'summary';
  const photoHeight = isSummary ? 0 : Math.round((width - 4) * POLAROID_IMAGE_RATIO);

  const dynamicStyles = useMemo(
    () =>
      StyleSheet.create({
        polaroid: {
          backgroundColor: themeColors.polaroidFrame,
          borderWidth: 1,
          borderColor: themeColors.polaroidFrameBorder,
          borderRadius: 0,
          padding: 2,
          ...getPolaroidShadowStyles(themeColors.polaroidShadow),
        },
        polaroidPlaceholder: {
          borderStyle: 'dashed',
          borderColor: themeColors.borderDefault,
        },
        caption: { backgroundColor: themeColors.polaroidCaptionBg, padding: 20 },
      }),
    [themeColors]
  );

  const polaroid = (
    <View
      style={[
        dynamicStyles.polaroid,
        placeholder && dynamicStyles.polaroidPlaceholder,
        isSummary && styles.polaroidSummary,
        !isSummary && { width, maxWidth: width },
        rotation !== '0deg' && { transform: [{ rotate: rotation }] },
        style,
      ]}
    >
      {isSummary ? (
        <View style={[styles.accentStrip, { backgroundColor: accentColor }]} />
      ) : (
        <View
          style={[
            styles.photoArea,
            { backgroundColor: accentColor, height: photoHeight },
          ]}
        >
          {imageArea}
        </View>
      )}
      <View style={[dynamicStyles.caption, isSummary && styles.captionSummary]}>{caption}</View>
    </View>
  );

  if (onPress || onLongPress) {
    return (
      <Pressable
        onPress={onPress}
        onLongPress={onLongPress}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? 'Card'}
        style={({ pressed }) => (pressed ? { opacity: 0.9 } : {})}
      >
        {polaroid}
      </Pressable>
    );
  }

  return polaroid;
}

// StyleSheet: RN shadow props and dynamic transform styles
const styles = StyleSheet.create({
  polaroidSummary: {
    marginBottom: 24,
    padding: 0,
    overflow: 'hidden',
  },
  accentStrip: {
    height: 6,
    width: '100%',
  },
  captionSummary: { paddingTop: 12, paddingHorizontal: 16, paddingBottom: 16 },
  photoArea: {
    width: '100%',
    padding: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
